import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import GroupsIcon from '@mui/icons-material/Groups';
import TuneIcon from '@mui/icons-material/Tune';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { SmartFilter } from '../../components/common/SmartFilter';
import {
  getAdminStaffList,
  AdminStaffMember,
  toggleAdminStaffActive,
  bulkDeleteAdminStaff,
} from '../../services/admin';
import {
  ADMIN_ROLES,
  AdminRole,
  countModuleAccess,
  DEPARTMENT_LABELS,
  getDepartmentLabel,
  ROLE_COLORS,
  ROLE_LABELS,
} from '../../utils/adminPermissions';
import useSnackbar from '../../hooks/useSnackbar';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import { useAuth } from '../../store/AuthContext';
import CreateUserDialog from './CreateUserDialog';

const ROLE_SORT_ORDER: Record<AdminRole, number> = {
  FOUNDER: 0,
  HOD: 1,
  SENIOR_MANAGER: 2,
  MANAGER: 3,
  EXECUTIVE: 4,
  TRAINEE: 5,
};

const sortStaffByRole = (members: AdminStaffMember[]) =>
  [...members].sort((a, b) => {
    const orderDiff =
      (ROLE_SORT_ORDER[a.role] ?? 99) - (ROLE_SORT_ORDER[b.role] ?? 99);
    if (orderDiff !== 0) return orderDiff;
    return (a.name || '').localeCompare(b.name || '');
  });

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  accent?: string;
}> = ({ label, value, icon, accent = '#3b82f6' }) => (
  <Paper
    sx={{
      p: 2,
      flex: 1,
      minWidth: 140,
      background: 'linear-gradient(135deg, #171717 0%, #1a1a1a 100%)',
      border: '1px solid #242424',
      borderRadius: 2,
    }}
  >
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${accent}18`,
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} color="#fff" lineHeight={1.2}>
          {value}
        </Typography>
        <Typography variant="caption" color="#64748b">
          {label}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const AdminStaff: React.FC = () => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const { isFounder, canCreate, canDelete, canEdit } = useAdminPermission();
  const canCreateStaff = canCreate('admin_staff');
  const canDeleteStaff = canDelete('admin_staff');
  const canEditStaff = canEdit('admin_staff');
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState<AdminStaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<AdminStaffMember | null>(
    null,
  );
  const [toggleLoading, setToggleLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const res = await getAdminStaffList();
      setStaff(sortStaffByRole(res?.data || []));
    } catch {
      showSnackbar('Failed to load admin roles', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = sortStaffByRole(staff);
    if (!q) return sorted;
    return sorted.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.role?.toLowerCase().includes(q) ||
        s.location?.name?.toLowerCase().includes(q),
    );
  }, [staff, search]);

  const stats = useMemo(() => {
    const customCount = staff.filter((s) => s.useCustomPermissions).length;
    const byRole = Object.values(ADMIN_ROLES).reduce(
      (acc, role) => {
        acc[role] = staff.filter((s) => s.role === role).length;
        return acc;
      },
      {} as Record<AdminRole, number>,
    );
    const disabledCount = staff.filter((s) => s.isActive === false).length;
    return { total: staff.length, customCount, byRole, disabledCount };
  }, [staff]);

  const openPermissionsScreen = (user: AdminStaffMember) => {
    navigate(`/admin-staff/${user._id}/permissions`);
  };

  const handleToggleActive = async () => {
    if (!toggleTarget) return;
    try {
      setToggleLoading(true);
      const res = await toggleAdminStaffActive(toggleTarget._id);
      const updated = res.data as AdminStaffMember;
      setStaff((prev) =>
        sortStaffByRole(
          prev.map((s) => (s._id === updated._id ? { ...s, ...updated } : s)),
        ),
      );
      showSnackbar(
        updated.isActive === false
          ? 'Admin user disabled successfully'
          : 'Admin user enabled successfully',
        'success',
      );
      setToggleTarget(null);
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to update admin status', 'error');
    } finally {
      setToggleLoading(false);
    }
  };

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  const isCurrentUser = (row: AdminStaffMember) =>
    row.email?.toLowerCase() === authState.userId?.toLowerCase();

  const selectableStaff = filteredStaff.filter((row) => !isCurrentUser(row));
  const isAllSelected =
    selectableStaff.length > 0 && selectedIds.length === selectableStaff.length;
  const isIndeterminate =
    selectedIds.length > 0 && selectedIds.length < selectableStaff.length;

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? selectableStaff.map((s) => s._id) : []);
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((i) => i !== id),
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      setBulkDeleteLoading(true);
      const result = await bulkDeleteAdminStaff(selectedIds);
      setStaff((prev) => prev.filter((s) => !selectedIds.includes(s._id)));
      showSnackbar(
        `${result.deletedCount} admin user(s) deleted successfully`,
        'success',
      );
      setSelectedIds([]);
      setBulkDeleteOpen(false);
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to delete admin users', 'error');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  return (
    <Box>
      {SnackbarComponent}
      <SmartFilter title="Admin Roles" createFilter={['SEARCH']} />
      <Box sx={{ p: 1 }}>
        <Box display="flex" gap={2} mb={2.5} flexWrap="wrap">
          <StatCard
            label="Total Admin Users"
            value={stats.total}
            icon={<GroupsIcon fontSize="small" />}
          />
          <StatCard
            label="Custom Permissions"
            value={stats.customCount}
            icon={<TuneIcon fontSize="small" />}
            accent="#f59e0b"
          />
          <StatCard
            label="Founders"
            value={stats.byRole.FOUNDER}
            icon={<ManageAccountsIcon fontSize="small" />}
            accent="#f59e0b"
          />
          <StatCard
            label="Disabled"
            value={stats.disabledCount}
            icon={<BlockIcon fontSize="small" />}
            accent="#ef4444"
          />
        </Box>

        <Paper
          sx={{
            p: 2,
            background: '#171717',
            border: '1px solid #242424',
            borderRadius: 2,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
            gap={1.5}
          >
            <Box>
              <Typography color="#fff" fontWeight={600}>
                Team Members
              </Typography>
              <Typography variant="caption" color="#64748b">
                Manage roles and fine-grained module permissions per user
              </Typography>
            </Box>
            <Box display="flex" gap={1.5} alignItems="center">
              <TextField
                size="small"
                placeholder="Search by name, email, role, location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260 }}
              />
              {canDeleteStaff && selectedIds.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Delete ({selectedIds.length})
                </Button>
              )}
              {canCreateStaff && (
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setCreateOpen(true)}
                >
                  Add User
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {canDeleteStaff && (
                    <TableCell
                      padding="checkbox"
                      sx={{ borderColor: '#242424' }}
                    >
                      <Checkbox
                        size="small"
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        sx={{
                          color: '#64748b',
                          '&.Mui-checked': { color: '#ef4444' },
                          '&.MuiCheckbox-indeterminate': { color: '#ef4444' },
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ color: '#64748b', borderColor: '#242424' }}>
                    User
                  </TableCell>
                  <TableCell sx={{ color: '#64748b', borderColor: '#242424' }}>
                    Role
                  </TableCell>
                  <TableCell sx={{ color: '#64748b', borderColor: '#242424' }}>
                    Department
                  </TableCell>
                  <TableCell sx={{ color: '#64748b', borderColor: '#242424' }}>
                    Location
                  </TableCell>
                  <TableCell sx={{ color: '#64748b', borderColor: '#242424' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ color: '#64748b', borderColor: '#242424' }}>
                    Permissions
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: '#64748b', borderColor: '#242424' }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={canDeleteStaff ? 8 : 7}
                      sx={{ color: '#64748b', borderColor: '#242424' }}
                    >
                      Loading admin roles...
                    </TableCell>
                  </TableRow>
                ) : filteredStaff.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canDeleteStaff ? 8 : 7}
                      sx={{ color: '#64748b', borderColor: '#242424' }}
                    >
                      No admin users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStaff.map((row) => {
                    const perms =
                      row.resolvedPermissions || row.defaultPermissions;
                    const accessCount = perms ? countModuleAccess(perms) : 0;
                    const isActive = row.isActive !== false;
                    return (
                      <TableRow
                        key={row._id}
                        hover
                        sx={{
                          opacity: isActive ? 1 : 0.55,
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                          cursor: 'pointer',
                        }}
                        onClick={() => openPermissionsScreen(row)}
                      >
                        {canDeleteStaff && (
                          <TableCell
                            padding="checkbox"
                            sx={{ borderColor: '#242424' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip
                              title={
                                isCurrentUser(row)
                                  ? 'You cannot delete your own account'
                                  : ''
                              }
                            >
                              <span>
                                <Checkbox
                                  size="small"
                                  checked={selectedIds.includes(row._id)}
                                  disabled={isCurrentUser(row)}
                                  onChange={(e) =>
                                    handleSelectOne(row._id, e.target.checked)
                                  }
                                  sx={{
                                    color: '#64748b',
                                    '&.Mui-checked': { color: '#ef4444' },
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell sx={{ borderColor: '#242424' }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: 14,
                                bgcolor: `${ROLE_COLORS[row.role] || '#64748b'}33`,
                                color: ROLE_COLORS[row.role] || '#94a3b8',
                              }}
                            >
                              {getInitials(row.name || '?')}
                            </Avatar>
                            <Box>
                              <Typography
                                color="#e2e8f0"
                                fontSize={14}
                                fontWeight={500}
                              >
                                {row.name}
                              </Typography>
                              <Typography color="#64748b" fontSize={12}>
                                {row.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ borderColor: '#242424' }}>
                          <Chip
                            label={ROLE_LABELS[row.role] || row.role}
                            size="small"
                            sx={{
                              bgcolor: `${ROLE_COLORS[row.role] || '#64748b'}22`,
                              color: ROLE_COLORS[row.role] || '#94a3b8',
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ color: '#94a3b8', borderColor: '#242424' }}
                        >
                          {getDepartmentLabel(row.department)}
                        </TableCell>
                        <TableCell
                          sx={{ color: '#94a3b8', borderColor: '#242424' }}
                        >
                          {row.location?.name || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ borderColor: '#242424' }}>
                          {(() => {
                            const canToggle =
                              canEditStaff &&
                              !isCurrentUser(row) &&
                              (isFounder || row.role !== ADMIN_ROLES.FOUNDER);
                            return (
                              <Chip
                                label={isActive ? 'Active' : 'Disabled'}
                                size="small"
                                clickable={canToggle}
                                onClick={
                                  canToggle
                                    ? (e) => {
                                        e.stopPropagation();
                                        setToggleTarget(row);
                                      }
                                    : undefined
                                }
                                title={
                                  canToggle
                                    ? isActive
                                      ? 'Click to disable admin user'
                                      : 'Click to enable admin user'
                                    : undefined
                                }
                                sx={{
                                  bgcolor: isActive ? '#10b98122' : '#ef444422',
                                  color: isActive ? '#10b981' : '#ef4444',
                                  fontWeight: 500,
                                  cursor: canToggle ? 'pointer' : 'default',
                                  '&:hover': canToggle
                                    ? {
                                        bgcolor: isActive
                                          ? '#10b98133'
                                          : '#ef444433',
                                      }
                                    : undefined,
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ borderColor: '#242424' }}>
                          <Box display="flex" gap={0.75} flexWrap="wrap">
                            <Chip
                              label={
                                row.useCustomPermissions
                                  ? 'Custom'
                                  : 'Role default'
                              }
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: row.useCustomPermissions
                                  ? '#f59e0b'
                                  : '#333',
                                color: row.useCustomPermissions
                                  ? '#f59e0b'
                                  : '#64748b',
                                height: 24,
                                fontSize: 11,
                              }}
                            />
                            <Chip
                              label={`${accessCount} grants`}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: '#333',
                                color: '#64748b',
                                height: 24,
                                fontSize: 11,
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ borderColor: '#242424' }}
                        >
                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            gap={0.5}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              onClick={() => openPermissionsScreen(row)}
                              sx={{ color: '#3b82f6' }}
                            >
                              <ManageAccountsIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>

        <CreateUserDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={fetchStaff}
          showSnackbar={showSnackbar}
        />

        <Dialog
          open={Boolean(toggleTarget)}
          onClose={() => !toggleLoading && setToggleTarget(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#171717',
              border: '1px solid #242424',
              color: '#fff',
            },
          }}
        >
          <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                border: '2px solid #E31E25',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              {toggleTarget?.isActive !== false ? (
                <BlockIcon sx={{ fontSize: 36, color: '#ef4444' }} />
              ) : (
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 36, color: '#10b981' }}
                />
              )}
            </Box>
            <Typography variant="h6" mb={1}>
              {toggleTarget?.isActive !== false ? 'Disable' : 'Enable'} admin
              user?
            </Typography>
            <Typography variant="body2" color="#94a3b8">
              {toggleTarget?.name} ({toggleTarget?.email}) will{' '}
              {toggleTarget?.isActive !== false
                ? 'lose access to the admin panel until re-enabled.'
                : 'be able to sign in again.'}
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{ px: 3, pb: 2.5, justifyContent: 'center', gap: 1 }}
          >
            <Button
              onClick={() => setToggleTarget(null)}
              disabled={toggleLoading}
              sx={{ color: '#94a3b8' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color={toggleTarget?.isActive !== false ? 'error' : 'success'}
              onClick={handleToggleActive}
              disabled={toggleLoading}
            >
              {toggleLoading
                ? 'Updating...'
                : toggleTarget?.isActive !== false
                  ? 'Disable'
                  : 'Enable'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={bulkDeleteOpen}
          onClose={() => !bulkDeleteLoading && setBulkDeleteOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#171717',
              border: '1px solid #242424',
              color: '#fff',
            },
          }}
        >
          <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                border: '2px solid #E31E25',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 36, color: '#ef4444' }} />
            </Box>
            <Typography variant="h6" mb={1}>
              Delete {selectedIds.length} admin user
              {selectedIds.length === 1 ? '' : 's'}?
            </Typography>
            <Typography variant="body2" color="#94a3b8">
              This will permanently revoke their access to the admin panel.
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{ px: 3, pb: 2.5, justifyContent: 'center', gap: 1 }}
          >
            <Button
              onClick={() => setBulkDeleteOpen(false)}
              disabled={bulkDeleteLoading}
              sx={{ color: '#94a3b8' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteSelected}
              disabled={bulkDeleteLoading}
            >
              {bulkDeleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AdminStaff;
